import { ref, onMounted, onUnmounted } from 'vue'

/**
 * ============================================================================
 * useHeaderFooterVisibility.js - Gestione Visibilità Header
 * ============================================================================
 * 
 * Composable Vue 3 che controlla quando nascondere l'header durante lo scroll.
 * 
 * LOGICA DI VISIBILITÀ:
 * L'header rimane VISIBILE finché:
 * • Utente NON ha scrollato (scrollY = 0) OPPURE
 * • Footer NON è ancora entrato in viewport
 * 
 * L'header diventa NASCOSTO quando ENTRAMBE le condizioni sono vere:
 * 1. Utente HA scrollato (scrollY > 0)
 * 2. Footer è ENTRATO in viewport (top ≤ window.innerHeight)
 * 
 * CASISTICHE:
 * • Page load (top): header VISIBILE (scrollY = 0)
 * • Scroll in mezzo page: header VISIBILE (footer non in vista)
 * • Scroll verso footer: header NASCOSTO (scrollY > 0 E footer in view)
 * • Scroll indietro su page: header VISIBILE di nuovo
 * 
 * UTILIZZO IN COMPONENTE:
 * const { showHeader } = useHeaderFooterVisibility()
 * 
 * <!-- In template: -->
 * <header v-show="showHeader" class="site-header">...</header>
 * 
 * INTEGRAZIONE:
 * Usato in SiteHeader.vue per controllare visibilità tramite v-show
 * Coordinato con heroGreenBar.js per transizioni smooth
 * ============================================================================
 */

/**
 * Composable per gestire la visibilità dell'header al scroll
 * 
 * @returns {Object} Oggetto reattivo con:
 *   - showHeader: Ref<boolean> - true se header deve essere visibile
 */
export function useHeaderFooterVisibility() {
  /**
   * Determina se mostrare l'header
   * Default: true (visibile al caricamento pagina)
   * Diventa false solo quando scrollato E footer in vista
   * 
   * @type {Ref<boolean>}
   */
  const showHeader = ref(true)

  /**
   * Verifica le condizioni di visibilità dell'header
   * 
   * LOGICA:
   * 1. Cerca elemento footer (.site-footer)
   * 2. Se footer non esiste → header sempre visibile
   * 3. Se footer esiste → calcola condizioni:
   *    - scrolled: window.scrollY > 0 (utente ha scrollato)
   *    - footerInView: footer.top ≤ window.innerHeight (footer in viewport)
   * 4. Header visibile se NOT (scrolled AND footerInView)
   */
  const checkFooterVisibility = () => {
    const footerEl = document.querySelector('footer.site-footer')
    if (!footerEl) {
      showHeader.value = true
      return
    }

    const { top } = footerEl.getBoundingClientRect()

    // Condizioni per nascondere l'header:
    // (a) utente ha scrollato (scrollY > 0), AND
    // (b) footer è nella viewport (top ≤ innerHeight)
    const scrolled = window.scrollY > 0
    const footerInView = top <= window.innerHeight

    showHeader.value = !(scrolled && footerInView)
  }

  /**
   * Setup lifecycle: mount listeners, calcolo iniziale
   */
  onMounted(() => {
    // Check iniziale
    checkFooterVisibility()

    // Listener scroll: aggiornamento in real-time (passive per performance)
    window.addEventListener('scroll', checkFooterVisibility, { passive: true })
  })

  /**
   * Cleanup lifecycle: rimuovi listeners
   */
  onUnmounted(() => {
    window.removeEventListener('scroll', checkFooterVisibility)
  })

  return { showHeader }
}